import React, {useEffect, useRef, useState} from 'react';
import {Animated, Easing, Image, Modal, Pressable, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Svg, {Circle, Line, Path, Polyline, Rect} from 'react-native-svg';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import {PlayerProfileFull} from '@/Hooks/PlayerProfileApi/types';
import type {ProfileViewAs} from '@/Hooks/PlayerProfileApi/useGetPlayerProfileApi';
import useGetPlayerGamesApi from '@/Hooks/PlayerProfileApi/useGetPlayerGamesApi';
import ChallengeModal from '../modals/ChallengeModal';
import ProfileButton from '../ui/ProfileButton';
import ThunderText from '../ui/ThunderText';
import RatingCard from '../ui/RatingCard';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import styles from './styles';

const SHEEN_WIDTH = 220;

// Diagonal light band that sweeps across the hero card (spec §5 "Hero sheen
// sweep": translateX -130%->340%, rotate 12deg, 7s ease-in-out infinite).
// `containerWidth` (the hero card's measured width, via onLayout) sets how
// far right the sweep travels -- a hardcoded pixel endpoint left the band
// stuck mid-card, still visible, on any viewport wider than that constant.
function HeroSheen({containerWidth}: {containerWidth: number}) {
  const x = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const anim = Animated.loop(
      Animated.timing(x, {
        toValue: 1,
        duration: 7000,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: false,
      }),
    );
    anim.start();
    return () => anim.stop();
  }, [x]);
  // Before the first layout pass, fall back to a width wide enough for a
  // typical viewport rather than 0 (which would park the sheen at the
  // card's left edge instead of off-screen).
  const endX = (containerWidth || 1200) + SHEEN_WIDTH;
  const translateX = x.interpolate({
    inputRange: [0, 0.55, 1],
    outputRange: [-SHEEN_WIDTH - 40, endX, endX],
  });
  return (
    <Animated.View
      pointerEvents="none"
      style={[styles.sheen, {transform: [{translateX}, {rotate: '12deg'}]}]}>
      {/* Soft-edged gradient, not a flat translucent block -- a hard-edged
          band reads as a glitchy stripe rather than a light sheen. */}
      <LinearGradient
        colors={[
          'rgba(250,207,71,0)',
          'rgba(250,207,71,0.2)',
          'rgba(250,207,71,0)',
        ]}
        locations={[0, 0.5, 1]}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 0}}
        style={{flex: 1}}
      />
    </Animated.View>
  );
}

// Rotating dashed-yellow ring around the avatar, matching the design's
// spinning ccSpin ring (stroke #FACF47, dasharray 24/10, r47 in a 100
// viewBox, 28s linear loop) -- not the static grey dashed border we had.
// The inner avatar sits inset ~8% (design: inset 9px on a 118px ring).
function AvatarRing({
  size,
  children,
}: {
  size: number;
  children: React.ReactNode;
}) {
  const spin = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const anim = Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: 28000,
        easing: Easing.linear,
        // react-native-web has no native driver; false avoids the warning.
        useNativeDriver: false,
      }),
    );
    anim.start();
    return () => anim.stop();
  }, [spin]);
  const rotate = spin.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });
  return (
    <View
      style={{
        width: size,
        height: size,
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Animated.View
        style={{
          position: 'absolute',
          width: size,
          height: size,
          transform: [{rotate}],
        }}>
        <Svg width={size} height={size} viewBox="0 0 100 100">
          <Circle
            cx={50}
            cy={50}
            r={47}
            fill="none"
            stroke="#FACF47"
            strokeWidth={2.6}
            strokeDasharray="24 10"
            strokeLinecap="round"
          />
        </Svg>
      </Animated.View>
      {children}
    </View>
  );
}

// Avatar-initials fallback, same approach as
// `src/Screens/Clubs/ClubProfile.tsx`'s member-list avatars.
const initials = (name?: string | null) =>
  (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

// Feather's "upload" glyph, redrawn directly with react-native-svg -- pulling
// in `@expo/vector-icons/Feather` drags in expo-font/expo-asset's native
// module loader, which crashes under Jest (no native linking in that env).
// react-native-svg has no such dependency (already used for the sparkline
// above), so a hand-drawn icon avoids the problem entirely.
function UploadIcon({size = 16, color = '#4D3F37'}: {size?: number; color?: string}) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Polyline
        points="17 8 12 3 7 8"
        stroke={color}
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Line
        x1="12"
        y1="3"
        x2="12"
        y2="15"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </Svg>
  );
}

// Small lock / globe glyph for the privacy toggle.
function PrivacyGlyph({isPrivate}: {isPrivate: boolean}) {
  return isPrivate ? (
    <Svg width={15} height={15} viewBox="0 0 24 24" fill="none">
      <Rect x={4} y={11} width={16} height={10} rx={2.5} stroke="#4D3F37" strokeWidth={2} />
      <Path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="#4D3F37" strokeWidth={2} />
    </Svg>
  ) : (
    <Svg width={15} height={15} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={9} stroke="#4D3F37" strokeWidth={2} />
      <Path
        d="M3 12h18M12 3c2.5 2.5 3.8 5.7 3.8 9S14.5 18.5 12 21c-2.5-2.5-3.8-5.7-3.8-9S9.5 5.5 12 3z"
        stroke="#4D3F37"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function KebabIcon({color = '#4D3F37'}: {color?: string}) {
  return (
    <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={5} r={2} fill={color} />
      <Circle cx={12} cy={12} r={2} fill={color} />
      <Circle cx={12} cy={19} r={2} fill={color} />
    </Svg>
  );
}

function EyeGlyph({color = '#4D3F37'}: {color?: string}) {
  return (
    <Svg width={15} height={15} viewBox="0 0 24 24" fill="none">
      <Path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
        stroke={color}
        strokeWidth={2}
      />
      <Circle cx={12} cy={12} r={3} stroke={color} strokeWidth={2} />
    </Svg>
  );
}

// Owner-only "more" menu, anchored to the kebab button right after the share
// icon. Combines what used to be two separate controls: the Public/Private
// toggle (was its own pill in the CTA row) and the "View as" preview picker
// (was a standalone bar above the whole header) -- both tucked away here to
// keep the CTA row from growing unbounded as more owner-only controls get
// added.
function ProfileMoreMenu({
  isPrivate,
  privacyPending,
  onTogglePrivacy,
  linkedAccountsVisible,
  linkedAccountsPending,
  onToggleLinkedAccountsVisibility,
  onViewAsChange,
}: {
  isPrivate: boolean;
  privacyPending: boolean;
  onTogglePrivacy?: () => void;
  linkedAccountsVisible: boolean;
  linkedAccountsPending: boolean;
  onToggleLinkedAccountsVisibility?: () => void;
  onViewAsChange?: (mode: ProfileViewAs) => void;
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{top: number; right: number} | null>(
    null,
  );
  const buttonRef = useRef<View>(null);

  const openMenu = () => {
    const node = buttonRef.current as unknown as HTMLElement | null;
    if (node && typeof node.getBoundingClientRect === 'function') {
      const rect = node.getBoundingClientRect();
      const viewportWidth =
        typeof window !== 'undefined' ? window.innerWidth : rect.right;
      // Right-aligned to the button's right edge (standard kebab-menu
      // direction, grows leftward) so it can't overflow the viewport's
      // right edge regardless of where the button sits.
      setAnchor({top: rect.bottom + 6, right: viewportWidth - rect.right});
    }
    setOpen(true);
  };

  return (
    <>
      <Pressable
        ref={buttonRef}
        testID="profile-more-menu"
        accessibilityLabel="More profile options"
        style={styles.shareButton}
        onPress={openMenu}>
        <KebabIcon />
      </Pressable>
      {/* Modal (not an in-tree dropdown) so this escapes the header card's
          overflow:hidden (needed for HeroSheen's sweep) -- a dropdown
          nested in the card got clipped whenever it ran past the card's
          bottom edge. Positioned via the button's own measured coordinates
          (getBoundingClientRect -- react-native-web forwards a Pressable's
          ref straight to its DOM node, so this is reliable) rather than a
          fixed corner, so it actually anchors to the kebab button. */}
      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.menuOverlay} onPress={() => setOpen(false)}>
          <Pressable
            style={[
              styles.menuDropdown,
              anchor
                ? {position: 'absolute', top: anchor.top, right: anchor.right}
                : {opacity: 0},
            ]}
            onPress={() => {}}
            testID="profile-more-menu-dropdown">
            {onTogglePrivacy ? (
              <Pressable
                testID="profile-privacy-toggle"
                disabled={privacyPending}
                style={[styles.menuItem, privacyPending && {opacity: 0.6}]}
                onPress={() => {
                  onTogglePrivacy();
                  setOpen(false);
                }}>
                <PrivacyGlyph isPrivate={isPrivate} />
                <CCText style={styles.menuItemText}>
                  {isPrivate ? 'Make profile public' : 'Make profile private'}
                </CCText>
              </Pressable>
            ) : null}
            {onToggleLinkedAccountsVisibility ? (
              <Pressable
                testID="profile-linked-accounts-visibility-toggle"
                disabled={linkedAccountsPending}
                style={[
                  styles.menuItem,
                  linkedAccountsPending && {opacity: 0.6},
                ]}
                onPress={() => {
                  onToggleLinkedAccountsVisibility();
                  setOpen(false);
                }}>
                <EyeGlyph />
                <CCText style={styles.menuItemText}>
                  {linkedAccountsVisible
                    ? 'Hide Lichess/Chess.com accounts'
                    : 'Show Lichess/Chess.com accounts'}
                </CCText>
              </Pressable>
            ) : null}
            <View style={styles.menuDivider} />
            <CCText style={styles.menuSectionLabel}>Preview as</CCText>
            <Pressable
              testID="view-as-member"
              style={styles.menuItem}
              onPress={() => {
                onViewAsChange?.('member');
                setOpen(false);
              }}>
              <EyeGlyph />
              <CCText style={styles.menuItemText}>Logged-in member</CCText>
            </Pressable>
            <Pressable
              testID="view-as-anonymous"
              style={styles.menuItem}
              onPress={() => {
                onViewAsChange?.('anonymous');
                setOpen(false);
              }}>
              <EyeGlyph />
              <CCText style={styles.menuItemText}>Anyone (public)</CCText>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

type Props = {
  profile: PlayerProfileFull;
  isOwner: boolean;
  navigation: any;
  isPrivate?: boolean;
  privacyPending?: boolean;
  onTogglePrivacy?: () => void;
  linkedAccountsVisible?: boolean;
  linkedAccountsPending?: boolean;
  onToggleLinkedAccountsVisibility?: () => void;
  onViewAsChange?: (mode: ProfileViewAs) => void;
};

export default function ProfileHeader({
  profile,
  isOwner,
  navigation,
  isPrivate = false,
  privacyPending = false,
  onTogglePrivacy,
  linkedAccountsVisible = false,
  linkedAccountsPending = false,
  onToggleLinkedAccountsVisibility,
  onViewAsChange,
}: Props) {
  const [showChallenge, setShowChallenge] = useState(false);
  const [heroCardWidth, setHeroCardWidth] = useState(0);
  // On mobile, the stats/CTA rows move out of identityCol into their own
  // full-width section below topRow -- nested under the avatar's indent,
  // they were squeezed into a narrow column and wrapped one stat per line.
  const {isMobileOrTab} = useMediaQuery();
  // results_breakdown is capped at the backend's recent-games window (20), so
  // won+drawn+lost reads as 20 for any player with 20+ lifetime games -- use
  // the Games tab's unwindowed pagination total instead, falling back to the
  // windowed sum only until that query resolves.
  const {data: gamesPage1} = useGetPlayerGamesApi(profile?.username);
  const windowedGamesCount =
    (profile.results_breakdown?.won ?? 0) +
    (profile.results_breakdown?.drawn ?? 0) +
    (profile.results_breakdown?.lost ?? 0);
  const gamesCount = gamesPage1?.total_items ?? windowedGamesCount;

  const winRatePct =
    profile.win_rate?.value != null
      ? `${Math.round(profile.win_rate.value * 100)}%`
      : '—';

  // Own toast instead of the browser's native alert() -- auto-dismisses so
  // the owner doesn't have to click anything to get rid of it.
  const [shareToast, setShareToast] = useState<'copied' | 'error' | null>(null);
  const onShare = async () => {
    const url = `${window.location.origin}/profile/${profile.username ?? ''}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareToast('copied');
    } catch (err) {
      setShareToast('error');
    }
    setTimeout(() => setShareToast(null), 2200);
  };

  const statsRowEl = (
    <View style={styles.statsRow}>
      <View style={styles.statChip}>
        <CCText style={styles.statLabel}>
          Games <CCText style={styles.statValue}>{gamesCount}</CCText>
        </CCText>
      </View>
      <View style={styles.statChip}>
        <CCText style={styles.statLabel}>
          Win rate <CCText style={styles.statValue}>{winRatePct}</CCText>
        </CCText>
      </View>
      <View style={styles.statChip}>
        <CCText style={styles.statLabel}>
          Streak{' '}
          <CCText style={styles.statValue}>{profile.streak_count ?? 0}</CCText>
        </CCText>
      </View>
      <View style={styles.statChip}>
        <CCText style={styles.statLabel}>
          Net score{' '}
          <CCText style={styles.statValue}>{profile.net_score ?? 0}</CCText>
        </CCText>
      </View>
    </View>
  );

  const ctaRowEl = (
    <View style={styles.ctaRow}>
      {isOwner ? (
        <>
          <ProfileButton
            variant="primary"
            onPress={() => navigation.navigate('AssessmentReport')}>
            Run assessment
          </ProfileButton>
          <ProfileButton
            variant="secondary"
            onPress={() => navigation.navigate('Games')}>
            Game history
          </ProfileButton>
          <ProfileButton
            variant="secondary"
            onPress={() => navigation.navigate('Settings')}>
            Edit profile
          </ProfileButton>
          <Pressable
            testID="profile-share-cta"
            style={styles.shareButton}
            onPress={onShare}>
            <UploadIcon />
          </Pressable>
          <ProfileMoreMenu
            isPrivate={isPrivate}
            privacyPending={privacyPending}
            onTogglePrivacy={onTogglePrivacy}
            linkedAccountsVisible={linkedAccountsVisible}
            linkedAccountsPending={linkedAccountsPending}
            onToggleLinkedAccountsVisibility={
              onToggleLinkedAccountsVisibility
            }
            onViewAsChange={onViewAsChange}
          />
        </>
      ) : (
        <>
          {/* Pre-disabled + reason (DND/self, though self never renders
              here) comes straight from the read payload -- can_challenge is
              re-checked server-side at send time regardless, per the plan
              ("never trust the client-side render alone"). challenge_pending
              is reload-safe (backed by the persisted ProfileChallenge
              record), so a reload after sending still shows "Pending". */}
          <ProfileButton
            variant="primary"
            testID="profile-challenge-cta"
            disabled={
              profile.challenge_pending || !profile.can_challenge?.allowed
            }
            onPress={() => setShowChallenge(true)}>
            {profile.challenge_pending
              ? 'Challenge pending'
              : profile.can_challenge?.allowed === false
              ? profile.can_challenge.reason || 'Challenge unavailable'
              : 'Challenge'}
          </ProfileButton>
          <Pressable
            testID="profile-share-cta"
            style={styles.shareButton}
            onPress={onShare}>
            <UploadIcon />
          </Pressable>
        </>
      )}
    </View>
  );

  return (
    <Box
      style={styles.card}
      onLayout={e => setHeroCardWidth(e.nativeEvent.layout.width)}>
      <HeroSheen containerWidth={heroCardWidth} />
      <View style={styles.heroRow}>
        <View style={styles.leftCol}>
          <View style={styles.topRow}>
            <AvatarRing size={118}>
              <View style={styles.avatar}>
                {profile.avatar_url ? (
                  <Image
                    source={{uri: profile.avatar_url}}
                    style={styles.avatarImage}
                  />
                ) : (
                  <ThunderText size={54} color="#fff">
                    {initials(profile.name || profile.username)}
                  </ThunderText>
                )}
              </View>
            </AvatarRing>
            <View style={styles.identityCol}>
              <View style={styles.nameRow}>
                <CCText fontWeight="semibold" style={styles.name}>
                  {profile.name || profile.username}
                </CCText>
                {/* FIDE chip only -- no level badge, role chip, or Gems/XP
                    pill anywhere on this header, per the plan's explicit cut
                    list. */}
                {profile.fide_title ? (
                  <View style={styles.fideChip}>
                    <CCText style={styles.fideChipText}>
                      {profile.fide_title}
                    </CCText>
                  </View>
                ) : null}
              </View>
              {/* One combined meta line (handle · location · member since),
                  per the design -- not three stacked lines. */}
              <CCText style={styles.memberSince} numberOfLines={1}>
                {[
                  profile.username ? `@${profile.username}` : null,
                  profile.location,
                  profile.member_since ? `Member since ${profile.member_since}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </CCText>

              {/* Nested inside identityCol (not a topRow/leftCol sibling) so
                  these share the name's indent, starting right of the
                  avatar -- per the design handoff, stat pills and the CTA
                  row are NOT flush under the avatar's left edge. Mobile is
                  the exception: identityCol is narrow there (full width
                  minus the avatar), so both move to their own full-width
                  section below topRow instead -- see statsRowEl/ctaRowEl. */}
              {!isMobileOrTab ? statsRowEl : null}
              {!isMobileOrTab ? ctaRowEl : null}
            </View>
          </View>
          {isMobileOrTab ? statsRowEl : null}
          {isMobileOrTab ? ctaRowEl : null}
        </View>

        <RatingCard
          rating={profile.gameplay_rating}
          history={profile.rating_history ?? []}
        />
      </View>

      {!isOwner && profile.user_id != null ? (
        <ChallengeModal
          visible={showChallenge}
          onClose={() => setShowChallenge(false)}
          username={profile.username ?? ''}
          opponentUserId={profile.user_id}
          opponentName={profile.name || profile.username || ''}
          navigation={navigation}
        />
      ) : null}

      {shareToast ? (
        <View style={styles.shareToast} testID="profile-share-toast">
          <CCText style={styles.shareToastText}>
            {shareToast === 'copied'
              ? 'Profile link copied to clipboard'
              : "Couldn't copy the link — try again"}
          </CCText>
        </View>
      ) : null}
    </Box>
  );
}
